import React from 'react'

import {
  CheckCircleIcon,
  HandThumbUpIcon,
  QuestionMarkCircleIcon,
} from '@heroicons/react/24/outline'
import _startCase from 'lodash/startCase'
import Image from 'next/image'
import { toast } from 'react-toastify'

import { DownloadSectionModal } from '../DownloadSectionModal'
import { DownloadOptionType, getApplicationPdfName } from '../dropDownMenuItems'
import { NoOfferAlert } from './NoOfferAlert'
import unlockLogo from '@/assets/icons/unlock_logo_sm.png'
import { Drawer } from '@/components/Drawer/DrawerV2'
import {
  DOCUMENT_CATEGORY,
  DOCUMENT_OPTIONS,
  DOCUMENT_TAG,
  DOCUMENT_TYPE,
} from '@/constants/documents'
import { Application, OfferCalculationsType } from '@/graphql/generated'
import { baseQueryConfig } from '@/services/config'
import { useGetDeclarationsByProfileQuery } from '@/services/declaration'
import {
  useDocumentUploadFinishMutation,
  useDocumentUploadStartMutation,
  useListUserDocumentQuery,
} from '@/services/documents'
import { useUploadFileMutation } from '@/services/s3'
import { useListUseOfProceedsQuery } from '@/services/useOfProceeds'
import { useGetApplicationProfilesQuery } from '@/services/v2-application/applicationProfile'
import { Declaration, UserDocument } from '@/types/unlock'
import { formatCurrency } from '@/utils'
import { DISCLOSURE_STATE } from '@/utils/checklistGroupMappings'
import { toastDefaultOptions } from '@/utils/toast'

const isMissing = (value: string | boolean | null | undefined, isRequired = true) => {
  if (!value || value == null) {
    if (!isRequired) {
      return <HandThumbUpIcon className='w-6 h-6 text-secondary mx-auto' />
    }
    return <QuestionMarkCircleIcon className='w-6 h-6 text-error mx-auto' />
  }
  return <CheckCircleIcon className='w-6 h-6 text-success mx-auto' />
}

export function GenerateApplicationDocs({
  application,
  applicationOffer,
  onClose,
  downloadingDoc,
}: {
  application: Application
  applicationOffer: OfferCalculationsType
  onClose: (e: React.MouseEvent<HTMLButtonElement>) => void
  downloadingDoc: (downloading: boolean) => void
}) {
  const [isOpen, setIsOpen] = React.useState(false)
  const [showingModal, setShowingModal] = React.useState(false)
  const [modalData, setModalData] = React.useState<{
    option: Partial<DownloadOptionType>
    docs: UserDocument[]
  }>()

  const { requestedInvestment, property } = application
  const { data: useOfProceeds } = useListUseOfProceedsQuery(application.id, {
    skip: !application.id,
  })
  const { data: applicationProfiles } = useGetApplicationProfilesQuery(
    { externalId: application.externalId },
    { skip: !application.externalId }
  )

  const mainApplicant = applicationProfiles?.results?.find((p) => p.position === 1)
  const coApplicant = applicationProfiles?.results?.find((p) => p.position === 2)

  const { data: declarations } = useGetDeclarationsByProfileQuery(
    {
      applicationProfileUUID: mainApplicant?.uuid,
      applicationUUID: application.uuid,
    },
    { skip: !mainApplicant?.uuid }
  )

  const { data: coDeclarations } = useGetDeclarationsByProfileQuery(
    {
      applicationProfileUUID: coApplicant?.uuid,
      applicationUUID: application.uuid,
    },
    { skip: !coApplicant?.uuid }
  )

  const { data: userDocs } = useListUserDocumentQuery(String(mainApplicant?.id), {
    skip: !mainApplicant,
  })

  const hasValidResidenceDeclaration = (declaration: Declaration) => {
    return (
      ['RESIDENCE', 'APPLICANT_CITIZENSHIP_STATUS'].includes(declaration.declaration_type) &&
      declaration.value === true
    )
  }

  const mainApplicantIsUSResident = declarations?.results?.some((d) =>
    hasValidResidenceDeclaration(d)
  )

  const coApplicantIsUSResident = coDeclarations?.results?.some((d) =>
    hasValidResidenceDeclaration(d)
  )

  const isSSNRequired = !['pending-qualification', 'pending-app-submit'].includes(
    application.applicationState
  )

  const baseFieldsToCheck = [
    applicationOffer?.selectedOffer?.offers,
    requestedInvestment,
    useOfProceeds,
    property?.titleVesting,
    property?.titleHeld,
    property?.propertyUsage,
    property?.propertyType,
    isSSNRequired ? mainApplicant?.profile?.social_security_number : true,
    mainApplicant?.profile?.email,
    mainApplicant?.profile?.marital_status,
    mainApplicantIsUSResident,
  ]

  const coApplicantFieldsToCheck = [
    isSSNRequired ? coApplicant?.profile?.social_security_number : true,
    coApplicant?.profile?.email,
    coApplicant?.profile?.marital_status,
    coApplicantIsUSResident,
  ]

  let fieldsToCheck = [...baseFieldsToCheck]

  if (coApplicant) {
    fieldsToCheck = [...baseFieldsToCheck, ...coApplicantFieldsToCheck]
  }

  const canGenerateApplication = fieldsToCheck.every((field) => {
    if (field == null) {
      return false
    }
    if (Array.isArray(field)) {
      return field.length > 0
    }
    if (typeof field === 'boolean') {
      return field === true
    }
    return true
  })

  const [documentUploadStart] = useDocumentUploadStartMutation()
  const [documentUploadFinish] = useDocumentUploadFinishMutation()
  const [uploadFile] = useUploadFileMutation()

  const handleDownloadFilesWithModal = async (downloading = false) => {
    const documentType = DISCLOSURE_STATE.includes(application.applicationState)
      ? DOCUMENT_OPTIONS.INITIAL_APPLICATION_UNLOCK_AGREEMENT
      : DOCUMENT_OPTIONS.FINAL_APPLICATION_FOR_UNLOCK_AGREEMENT

    const existingDocs = userDocs.filter((it) => it.document === documentType)

    const docCount = existingDocs.length

    if (!docCount) {
      handleGenerateApplicationPdf(downloading)
    } else {
      setModalData({
        option: { documentType: documentType },
        docs: existingDocs,
      })
      setShowingModal(true)
    }
  }

  const handleGenerateApplicationPdf = async (downloading = false) => {
    let headers = {}
    try {
      const jwt = localStorage.getItem('jwt')
      if (!jwt) {
        throw new Error('Unable to authenticate')
      }
      headers = { Authorization: `JWT ${jwt}` }
      if (downloading) {
        downloadingDoc(true)
      }
      const { fileName, documentType } = getApplicationPdfName(application)
      const appPdfUrl = `${baseQueryConfig.baseUrl}/v1/application/${application.externalId}/pdf/`
      const resp = await fetch(appPdfUrl, { headers })
      const blob = await resp.blob()
      const file = new File([blob], 'application.pdf', { type: 'application/pdf' })

      const payload = {
        file_name: fileName,
        application_profile: mainApplicant?.id,
        document: documentType,
        tag: DOCUMENT_TAG.DEAL_FILES,
        customer_visible: false,
        category: DOCUMENT_CATEGORY.DISCLOSURE,
        type: DOCUMENT_TYPE.APPLICATION,
      }

      const { url, uuid } = await documentUploadStart(payload).unwrap()
      await uploadFile({ url, file })
      await documentUploadFinish(uuid).unwrap()
      toast.success('Application PDF generated successfully', {
        ...toastDefaultOptions,
        className: 'min-w-max',
      })
      setIsOpen(false)
    } catch (error) {
      console.error('Error generating PDF:', error)
    } finally {
      if (downloading) {
        downloadingDoc(false)
      }
    }
  }

  const handleOpen = (e) => {
    if (canGenerateApplication) {
      handleDownloadFilesWithModal(true)
    } else {
      onClose(e)
      setTimeout(() => {
        setIsOpen(true)
      }, 190)
    }
  }

  return (
    <>
      {showingModal && (
        <DownloadSectionModal
          onClose={() => setShowingModal(false)}
          onDownload={async () => await handleGenerateApplicationPdf(true)}
          data={modalData}
        />
      )}
      <button
        type='button'
        className='flex w-full px-4 py-5 text-sm hover:bg-slate-50 hover:font-semibold'
        onClick={handleOpen}
      >
        <Image
          src={unlockLogo}
          alt='Unlock Logo'
          className='w-5 h-5 mr-2'
        />
        <span>Application</span>
      </button>

      <Drawer
        title='Generate Application'
        isOpen={isOpen}
        setIsOpen={setIsOpen}
      >
        {!applicationOffer?.selectedOffer?.offers ? (
          <NoOfferAlert
            onClose={() => {
              setIsOpen(false)
            }}
          />
        ) : (
          <>
            <blockquote className='prose prose-sm border-l-4 pl-3'>
              Please ensure that all the required information is filled out in the Application.
            </blockquote>

            <h3 className='text-lg mt-3'>Investment</h3>
            <table className='table table-sm table-zebra mt-3'>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Value</th>
                  <th className='text-center'>Completed</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Requested Investment</td>
                  <td>
                    {requestedInvestment ? formatCurrency({ amount: requestedInvestment }) : null}
                  </td>
                  <td>
                    {isMissing(requestedInvestment !== null && requestedInvestment !== undefined)}
                  </td>
                </tr>
                <tr>
                  <td>Use of proceeds</td>
                  <td>
                    <div className='flex flex-wrap gap-1'>
                      {useOfProceeds?.map((item) => (
                        <div
                          key={item.id}
                          className='badge badge-neutral'
                        >
                          {_startCase(item.name)}
                        </div>
                      ))}
                    </div>
                  </td>
                  <td>{isMissing(useOfProceeds?.length > 0)}</td>
                </tr>
                <tr>
                  <td>Title will be held in what names</td>
                  <td>{property?.titleVesting}</td>
                  <td>{isMissing(property?.titleVesting)}</td>
                </tr>
                <tr>
                  <td>Manner in which title will be held</td>
                  <td>{_startCase(property?.titleHeld)}</td>
                  <td>{isMissing(property?.titleHeld)}</td>
                </tr>
                <tr>
                  <td>Property Use</td>
                  <td>{_startCase(property?.propertyUsage)}</td>
                  <td>{isMissing(property?.propertyUsage)}</td>
                </tr>
                <tr>
                  <td>Property Type</td>
                  <td>{_startCase(property?.propertyType)}</td>
                  <td>{isMissing(property?.propertyType)}</td>
                </tr>
              </tbody>
            </table>

            <h3 className='text-lg mt-5'>Applicant</h3>
            <table className='table table-sm table-zebra mt-3'>
              <tbody>
                <tr>
                  <td>SSN</td>
                  <td>{mainApplicant?.profile?.social_security_number}</td>
                  <td>
                    {isMissing(mainApplicant?.profile?.social_security_number, isSSNRequired)}
                  </td>
                </tr>
                <tr>
                  <td>Email</td>
                  <td className='max-w-4 overflow-clip overflow-ellipsis'>
                    {mainApplicant?.profile?.email}
                  </td>
                  <td className='overflow-hidden whitespace-nowrap'>
                    {isMissing(mainApplicant?.profile?.email)}
                  </td>
                </tr>
                <tr>
                  <td>Marital Status</td>
                  <td>{_startCase(mainApplicant?.profile?.marital_status)}</td>
                  <td>{isMissing(mainApplicant?.profile?.marital_status)}</td>
                </tr>
                <tr>
                  <td>Declarations</td>
                  <td>{mainApplicantIsUSResident ? 'OK' : 'Missing'}</td>
                  <td>{isMissing(mainApplicantIsUSResident)}</td>
                </tr>
              </tbody>
            </table>
            {coApplicant && (
              <details
                className='mt-5'
                open
              >
                <summary>Co-Applicants Fields</summary>
                <table className='table table-sm table-zebra mt-3'>
                  <tbody>
                    <tr>
                      <td className='w-2/6'>SSN</td>
                      <td className='w-3/6'>{coApplicant?.profile?.social_security_number}</td>
                      <td className='w-1/6'>
                        {isMissing(mainApplicant?.profile?.social_security_number, isSSNRequired)}
                      </td>
                    </tr>
                    <tr>
                      <td>Email</td>
                      <td>{coApplicant?.profile?.email}</td>
                      <td>{isMissing(coApplicant?.profile?.email)}</td>
                    </tr>
                    <tr>
                      <td>Marital Status</td>
                      <td>{_startCase(coApplicant?.profile?.marital_status)}</td>
                      <td>{isMissing(coApplicant?.profile?.marital_status)}</td>
                    </tr>
                    <tr>
                      <td>Declarations</td>
                      <td>{coApplicantIsUSResident ? 'OK' : 'Missing'}</td>
                      <td>{isMissing(coApplicantIsUSResident)}</td>
                    </tr>
                  </tbody>
                </table>
              </details>
            )}
          </>
        )}
      </Drawer>
    </>
  )
}
